import { useTranslation } from "@/hooks/useTranslation";
import { useUser } from "@clerk/react";
import { ReactNode, useState } from "react";
import { Trans } from "react-i18next";

/** Word picked out of the heading: bold, in the brand gradient. */
export function GradientHighlight({ children }: { children?: ReactNode }) {
  return (
    <span className="font-bold bg-gradient-to-r from-majorelle to-pink-500 bg-clip-text text-transparent">
      {children}
    </span>
  );
}

/**
 * Heading the empty desktop chat opens on: one greeting drawn at random among
 * those the locale offers, addressed to the signed-in user. Visitors we have no
 * first name for get the neutral wording instead, which reads the same but
 * greets nobody.
 */
export function ChatNewGreeting() {
  const { t } = useTranslation();
  const { user } = useUser();

  // Drawn once per mount: the greeting stays put while the user reads and
  // types, and a new chat opens on a different one.
  const [draw] = useState(Math.random);
  const greetings = t("chat.new.title.greetings", {
    returnObjects: true,
  }) as string[];
  const greetingIndex = Math.floor(draw * greetings.length);

  const firstName = user?.firstName?.trim();

  return (
    <div className="flex flex-col gap-3">
      <h1 className="text-3xl font-normal tracking-tight">
        {firstName ? (
          <Trans
            i18nKey={`chat.new.title.greetings.${greetingIndex}`}
            values={{ name: firstName }}
            components={{ hl: <GradientHighlight /> }}
          />
        ) : (
          <>
            {t("chat.new.title.lead")}{" "}
            <GradientHighlight>
              {t("chat.new.title.highlight")}
            </GradientHighlight>
            {t("chat.new.title.tail")}
          </>
        )}
      </h1>
      <p className="text-sm text-muted-foreground">
        {t("chat.new.description")}
      </p>
    </div>
  );
}
